import React from "react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";

const InfoTip = ({ text }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] rounded" aria-label="Informazioni">
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px] text-xs leading-relaxed">
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const ParameterControl = ({ param, value, onChange }) => {
  const isInt = param.type === "int";
  const tid = `param-${param.key}`;

  const Header = (
    <div className="flex items-start gap-2">
      <div className="min-w-0">
        <Label htmlFor={tid} className="text-sm font-medium leading-tight">
          {param.label}
        </Label>
        <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{param.help}</p>
      </div>
      <div className="mt-0.5">
        <InfoTip text={param.help} />
      </div>
    </div>
  );

  // BOOLEAN
  if (param.type === "bool") {
    return (
      <div className="flex items-center justify-between gap-3 py-1">
        <div className="min-w-0 flex items-start gap-2">
          <div className="min-w-0">
            <Label htmlFor={tid} className="text-sm font-medium">{param.label}</Label>
            <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{param.help}</p>
          </div>
        </div>
        <Switch
          id={tid}
          data-testid={tid}
          checked={!!value}
          onCheckedChange={(v) => onChange(v)}
        />
      </div>
    );
  }

  // ENUM
  if (param.type === "enum") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-2 md:gap-4 items-start py-1">
        {Header}
        <Select value={String(value)} onValueChange={(v) => onChange(v)}>
          <SelectTrigger data-testid={tid} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(param.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // STRING (free text, e.g. CSV file name)
  if (param.type === "string") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-2 md:gap-4 items-start py-1">
        {Header}
        <div>
          <Input
            id={tid}
            data-testid={tid}
            type="text"
            className="w-full font-mono text-sm"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={param.recommended}
          />
          <div className="mt-1 flex items-center justify-end">
            <Badge variant="secondary" className="text-[10px] md:text-xs font-mono">
              Consigliato: {param.recommended}
            </Badge>
          </div>
        </div>
      </div>
    );
  }

  // NUMERIC (int / double)
  const min = param.min;
  const max = param.max;
  const showSlider = min != null && max != null && !param.noSlider;
  const step = param.step || (isInt ? 1 : 0.1);

  const handleNumber = (raw) => {
    if (raw === "") {
      onChange("");
      return;
    }
    const num = isInt ? parseInt(raw, 10) : parseFloat(raw);
    if (!Number.isNaN(num)) onChange(num);
  };

  const handleBlur = () => {
    let num = Number(value);
    if (Number.isNaN(num)) num = min != null ? min : 0;
    if (min != null && num < min) num = min;
    if (max != null && num > max) num = max;
    if (isInt) num = Math.round(num);
    onChange(num);
  };

  const sliderVal = Number.isNaN(Number(value)) ? (min || 0) : Number(value);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-2 md:gap-4 items-start py-1">
      {Header}
      <div>
        <div className="flex items-center gap-3">
          {showSlider && (
            <Slider
              className="flex-1"
              data-testid={`${tid}-slider`}
              min={min}
              max={max}
              step={step}
              value={[sliderVal]}
              onValueChange={(arr) => onChange(arr[0])}
            />
          )}
          <Input
            id={tid}
            data-testid={tid}
            type="number"
            inputMode="decimal"
            className="w-24 font-mono text-sm"
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(e) => handleNumber(e.target.value)}
            onBlur={handleBlur}
          />
        </div>
        <div className="mt-1 flex items-center justify-between">
          {showSlider ? (
            <span className="text-[10px] md:text-xs text-muted-foreground font-mono">{min} – {max}</span>
          ) : <span />}
          <Badge variant="secondary" className="text-[10px] md:text-xs font-mono">
            Oro: {param.recommended}
          </Badge>
        </div>
      </div>
    </div>
  );
};
